// SOPDialog.js
// Extracted from AIWorkforcePage.js for better maintainability

import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Textarea } from '../../../components/ui/textarea';
import { Label } from '../../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { Badge } from '../../../components/ui/badge';
import { FileText, Plus, Trash2, CheckCircle, Bot } from 'lucide-react';
import { SOP_TRIGGER_TYPES, SOP_ACTION_TYPES } from '../constants';

/**
 * Dialog for creating/editing Standard Operating Procedures
 * 
 * @param {Object} props
 * @param {boolean} props.open - Controls dialog visibility
 * @param {function} props.onOpenChange - Callback when dialog open state changes
 * @param {Object} props.formData - Current form data (or editingSOP merged)
 * @param {function} props.onFormChange - Callback when form data changes
 * @param {Object|null} props.editingSOP - SOP being edited (null for new)
 * @param {function} props.onEditingSOPChange - Callback when editing SOP changes
 * @param {Array} props.agents - List of available AI agents
 * @param {function} props.onSave - Callback when save is clicked
 * @param {function} props.onCancel - Callback when cancel is clicked
 */
export const SOPDialog = ({
  open,
  onOpenChange,
  formData,
  onFormChange,
  editingSOP,
  onEditingSOPChange,
  agents = [],
  onSave,
  onCancel
}) => {
  // Helper to get current value (from editingSOP if editing, otherwise formData)
  const getValue = (field) => editingSOP?.[field] ?? formData[field];
  
  // Helper to set value
  const setValue = (field, value) => {
    if (editingSOP) {
      onEditingSOPChange({ ...editingSOP, [field]: value });
    } else {
      onFormChange({ ...formData, [field]: value });
    }
  };

  const getSteps = () => editingSOP?.steps || formData.steps || [];

  const handleAddStep = () => {
    const newStep = { title: '', description: '', action_type: 'process' };
    const steps = [...getSteps(), newStep];
    if (editingSOP) {
      onEditingSOPChange({ ...editingSOP, steps });
    } else {
      onFormChange({ ...formData, steps });
    }
  };

  const handleRemoveStep = (idx) => {
    const steps = getSteps().filter((_, i) => i !== idx);
    const newSteps = steps.length ? steps : [{ title: '', description: '', action_type: 'process' }];
    if (editingSOP) {
      onEditingSOPChange({ ...editingSOP, steps: newSteps });
    } else {
      onFormChange({ ...formData, steps: newSteps });
    }
  };

  const handleStepChange = (idx, field, value) => {
    const steps = [...getSteps()];
    steps[idx][field] = value;
    if (editingSOP) {
      onEditingSOPChange({ ...editingSOP, steps });
    } else {
      onFormChange({ ...formData, steps });
    }
  };

  const toggleAgent = (agentId) => {
    const currentAgents = getValue('assigned_agents') || [];
    const newAgents = currentAgents.includes(agentId)
      ? currentAgents.filter(id => id !== agentId)
      : [...currentAgents, agentId];
    setValue('assigned_agents', newAgents);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            {editingSOP ? 'Edit SOP' : 'Create SOP'}
          </DialogTitle>
          <DialogDescription>
            Define standard operating procedures for AI agents
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>SOP Name *</Label>
              <Input
                value={getValue('name') || ''}
                onChange={(e) => setValue('name', e.target.value)}
                placeholder="e.g., Lead Qualification Process"
                data-testid="sop-name"
              />
            </div>
            <div>
              <Label>Trigger Type</Label>
              <Select 
                value={getValue('trigger_type') || 'manual'} 
                onValueChange={(val) => setValue('trigger_type', val)}
              >
                <SelectTrigger data-testid="sop-trigger-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SOP_TRIGGER_TYPES.map(type => (
                    <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label>Description</Label>
            <Textarea
              value={getValue('description') || ''}
              onChange={(e) => setValue('description', e.target.value)}
              placeholder="Describe the purpose and scope of this SOP..."
              rows={2}
              data-testid="sop-description"
            />
          </div>

          {/* Assign to Agents */}
          <div>
            <Label className="flex items-center gap-2">
              <Bot className="h-4 w-4" />
              Assign to AI Agents
            </Label>
            <p className="text-xs text-muted-foreground mb-2">Select which AI agents can execute this SOP</p>
            <div className="flex flex-wrap gap-2">
              {agents.map(agent => (
                <Badge
                  key={agent.id}
                  variant={getValue('assigned_agents')?.includes(agent.id) ? 'default' : 'outline'}
                  className={`cursor-pointer ${getValue('assigned_agents')?.includes(agent.id) ? 'bg-violet-500' : ''}`}
                  onClick={() => toggleAgent(agent.id)}
                  data-testid={`sop-agent-${agent.id}`}
                >
                  {agent.name}
                </Badge>
              ))}
            </div>
          </div>

          {/* Steps */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label>Steps</Label>
              <Button variant="outline" size="sm" onClick={handleAddStep} data-testid="sop-add-step">
                <Plus className="h-4 w-4 mr-1" />
                Add Step
              </Button>
            </div>
            <div className="space-y-3">
              {getSteps().map((step, idx) => (
                <div key={idx} className="p-3 border rounded-lg bg-slate-50 space-y-2" data-testid={`sop-step-${idx}`}>
                  <div className="flex items-center gap-2">
                    <div className="h-6 w-6 rounded-full bg-violet-500 text-white flex items-center justify-center text-xs font-bold">
                      {idx + 1}
                    </div>
                    <Input
                      value={step.title || ''}
                      onChange={(e) => handleStepChange(idx, 'title', e.target.value)}
                      placeholder={`Step ${idx + 1} title`}
                      className="flex-1"
                    />
                    <Select
                      value={step.action_type || 'process'}
                      onValueChange={(val) => handleStepChange(idx, 'action_type', val)}
                    >
                      <SelectTrigger className="w-[120px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {SOP_ACTION_TYPES.map(type => (
                          <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button variant="ghost" size="sm" onClick={() => handleRemoveStep(idx)}>
                      <Trash2 className="h-4 w-4 text-red-500" />
                    </Button>
                  </div>
                  <Textarea
                    value={step.description || ''}
                    onChange={(e) => handleStepChange(idx, 'description', e.target.value)}
                    placeholder="Step description and instructions..."
                    rows={2}
                  />
                </div>
              ))}
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onCancel} data-testid="sop-cancel">Cancel</Button>
          <Button onClick={onSave} data-testid="sop-save">
            <CheckCircle className="h-4 w-4 mr-2" />
            {editingSOP ? 'Update SOP' : 'Create SOP'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default SOPDialog;
